import React, { useState, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import { EQUIPMENT_CODES_TYPEAHEAD_OPTIONS_SEARCH } from '../../../../services/data-store.gql';
import useQuery from '../../../../hooks/useQuery';
import { useFederalStandards } from '../../fvs-provider';
import { useFvsRequirements } from '../requirements-provider';
import { getFilterStructure } from './requirment-filters/requirment-filter-structure';

const RequirementsFilter = ({ setFilters }) => {
  const {
    applicableFvsYears,
    selectedOptions,
    selectedYear,
    setFvsContextData,
    standardItemsAsOptions,
  } = useFederalStandards();

  const { FilterPanel: RequirmentsFilterPanel } = FilterPanel;

  const { lookupCodes, setFvsRequirementsContextData } = useFvsRequirements();
  const query = useQuery();
  const year = query.get('filter-year');
  const standardItemNumber = query.get('filter-standardItem');



  const defaultFilters = useMemo(() => ({
    year: year || selectedYear?.toString() || '',
    standardItem: selectedOptions?.standardItemNumber || standardItemNumber || '',
  }), [selectedYear, selectedOptions?.standardItemNumber, standardItemNumber, year]);

  const [typeaheadData, setTypeaheadData] = useState(null);
  const [searchKey, setSearchKey] = useState(null);

  const [getTypeAheadOptions] = useLazyQuery(
    EQUIPMENT_CODES_TYPEAHEAD_OPTIONS_SEARCH,
    {
      fetchPolicy: 'network-only',
      onCompleted: ({ getEquipmentCodesTypeAheadOptions }) => {
        if (getEquipmentCodesTypeAheadOptions) {
          const { options } = getEquipmentCodesTypeAheadOptions;
          // return unique options only.
          const uniqueOptions = Array.from(new Set(options));
          setTypeaheadData({
            field: searchKey,
            values: uniqueOptions,
          });
        }
      },
    },
  );

  const removeReqFilterTitlePrefix = (f) =>
    f.key?.includes('req_') ? f.key.split('_')[1] : f.key;

  const handleFiltersChange = (updatedFilters) => {
    const filters = {
      operator: 'AND',
      value: [],
    };

    const filterByKeys = updatedFilters
      ?.map((f) => ({ ...f, key: removeReqFilterTitlePrefix(f) }))
      ?.reduce((filterObj, currentFilterObj) => {
        return { ...filterObj, [currentFilterObj?.key]: currentFilterObj };
      }, {});

    // eslint-disable-next-line
    const state = {
      year: Number.parseInt(filterByKeys?.year?.value, 10) || year,
      standardItem: filterByKeys?.standardItem?.value || standardItemNumber,
    };

    // EARLY RETURN: Only proceed if standardItem is present
    const isYearValid = !isNaN(state.year) && state.year > 0;
    const isStandardItemValid =
      state.standardItem && state.standardItem.toString().trim();

    if (!isYearValid || !isStandardItemValid) {
      // Don't process or call setFilters if required values are missing
      return;
    }

      setFvsContextData?.('SET_SELECTED_YEAR', state.year);
      setFvsContextData('SET_SELECTED_OPTIONS', {
        ...selectedOptions,
        year: state.year,
        standardItemNumber: state.standardItem,
      });
      setFvsRequirementsContextData('SET_EQUIPMENT_ASSOCIATION_LIST', {
        rows: [],
      });

    if (filterByKeys.associationType) {
      filters.value.push({
        key: 'associationTypeCode',
        operator: 'EQ',
        value: filterByKeys.associationType?.value,
      });
    }

    if (filterByKeys.category) {
      filters.value.push({
        key: '$equipmentCode.category$',
        operator: 'EQ',
        value: filterByKeys.category?.value,
      });
    }

    if (filterByKeys.title) {
      filters.value.push({
        key: '$equipmentCode.title$',
        operator: 'EQ',
        value: filterByKeys.title?.value,
      });
    }

    if (filterByKeys.code) {
      filters.value.push({
        key: '$equipmentCode.code$',
        operator: 'EQ',
        value: filterByKeys.code?.value,
      });
    }

    // Add year filter
    const yearValue = Number.parseInt(state.year, 10);
    if (!isNaN(yearValue) && yearValue > 0) {
      filters.value.push({
        key: '$standardItem.year$',
        operator: 'EQ',
        value: yearValue,
      });
    }

    // Add standardItem filter
    if (state.standardItem && state.standardItem.toString().trim()) {
      setFvsContextData?.('SET_SELECTED_STANDARD_ITEM', state.standardItem);
      filters.value.push({
        key: '$standardItem.standard_item_number$',
        operator: 'EQ',
        value: state.standardItem,
      });
    }

    filters.value.push(
      {
        key: '$equipmentCode.is_active$',
        operator: 'EQ',
        value: 1,
      },
      {
        key: '$equipmentCode.deleted_at$',
        operator: 'EQ',
        value: null,
      },
    );

    setTypeaheadData(null);
    setFilters(filters);
  };

  const handleFetchTypeaheads = ({ variables: queryVariables }) => {
    const selectedKey = queryVariables?.field;
    const filterObj = queryVariables?.filters[0]?.conditions?.find(
      (filter) => filter.key === selectedKey,
    );

    setSearchKey(selectedKey);
    getTypeAheadOptions({
      variables: {
        key: selectedKey,
        search: filterObj.value,
        filters: [
          {
            operator: '$and',
            conditions: [
              { operator: '$exact', key: 'year', value: `${selectedYear || year}` },
            ],
          },
        ],
      },
    });
  };

  return (
    <>
      <RequirmentsFilterPanel
        key={`${defaultFilters.year}-${defaultFilters.standardItem}`}
        clearButtonLabel="Reset All"
        filterStructure={getFilterStructure(
          applicableFvsYears,
          defaultFilters,
          standardItemsAsOptions,
          lookupCodes,
        )}
        model="standardItem"
        order="standardItemNumber ASC"
        setQueryFiltersState={handleFiltersChange}
        fetchTypeaheads={handleFetchTypeaheads}
        typeaheadData={typeaheadData}
        showClearIcon
        retainFilters
      />
    </>
  );
};

RequirementsFilter.defaultProps = {};

RequirementsFilter.propTypes = {
  setFilters: PropTypes.func.isRequired,
};

export default RequirementsFilter;
